import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

const loader = new GLTFLoader();

// Cache per URL — supaya model yang sama (dipasang berkali-kali di scene
// atau dipasang ulang setelah hapus/undo) tidak di-fetch & di-parse ulang
// dari server setiap kali. Menyimpan PROMISE (bukan hasil jadi), supaya
// pemanggilan yang tumpang tindih saat masih loading tetap nyambung ke
// request yang sama, tidak duplikat fetch.
const modelCache = new Map();

function loadGLTFRaw(url) {
  if (modelCache.has(url)) return modelCache.get(url);

  const promise = new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => resolve(gltf.scene),
      undefined,
      (err) => reject(err)
    );
  });

  modelCache.set(url, promise);
  return promise;
}

/**
 * Load model .glb dari URL, lalu auto-scale & posisikan supaya:
 * - Bounding box model pas sesuai targetWidth/targetHeight/targetDepth
 *   (dalam meter, sesuai field width/height/depth produk di database)
 * - Origin (titik 0,0,0) berada di TENGAH bawah model (mengikuti
 *   konvensi geometri hardcoded yang sudah ada di addDevice(), supaya
 *   logic positioning wall/table/ceiling/floor tidak perlu diubah)
 * - Model selalu menghadap +Z sebagai "depan" (asumsi yang disepakati).
 *   Kalau file .glb aslinya punya orientasi "depan" yang berbeda (umum
 *   terjadi karena tiap software/sumber 3D punya konvensi sendiri),
 *   koreksi lewat rotationYDeg (field model_rotation_y di database,
 *   diatur admin lewat slider di ProductForm) — TIDAK perlu edit ulang
 *   file .glb-nya.
 *
 * extraScale: faktor koreksi manual dari field model_scale produk,
 * dipakai kalau auto-scaling berdasar bounding box masih kurang pas
 * (misal model punya banyak rongga/detail kecil yang bikin bounding
 * box tidak representatif).
 *
 * rotationYDeg: rotasi tambahan di sumbu Y (derajat), diterapkan SEBELUM
 * auto-fit bounding box, supaya tetap akurat untuk model yang bentuknya
 * tidak simetris (mis. kamera dengan lensa menonjol ke satu sisi).
 */
export async function loadScaledModel(url, targetWidth, targetHeight, targetDepth, extraScale = 1, rotationYDeg = 0) {
  const original = await loadGLTFRaw(url);
  // Clone supaya tiap pemasangan di scene punya instance object3D sendiri
  // (posisi/rotasi per device tidak saling memengaruhi), walau geometry
  // & material di baliknya tetap di-share (efisien memori).
  const model = original.clone(true);

  model.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });

  // Bungkus dalam wrapper group supaya rotasi koreksi diterapkan dulu,
  // baru bounding box (untuk auto-scale & recenter) dihitung dari hasil
  // yang SUDAH terkoreksi orientasinya — bukan dari pose asli file.
  const wrapper = new THREE.Group();
  wrapper.add(model);
  if (rotationYDeg) {
    model.rotation.y = (rotationYDeg * Math.PI) / 180;
  }

  // Hitung bounding box (setelah rotasi koreksi) untuk auto-scaling
  const box = new THREE.Box3().setFromObject(wrapper);
  const size = new THREE.Vector3();
  box.getSize(size);

  const safe = (v) => (v > 0.0001 ? v : 1);
  const scaleX = targetWidth / safe(size.x);
  const scaleY = targetHeight / safe(size.y);
  const scaleZ = targetDepth / safe(size.z);

  // Pakai scale UNIFORM (nilai terkecil dari 3 sumbu) supaya proporsi model
  // tidak gepeng/melar tidak wajar — lalu dikalikan extraScale untuk koreksi
  // manual dari admin kalau hasilnya masih kurang pas.
  const uniformScale = Math.min(scaleX, scaleY, scaleZ) * extraScale;
  wrapper.scale.setScalar(uniformScale);

  // Re-hitung bounding box SETELAH scaling, untuk recenter origin ke
  // tengah-bawah (x=0 tengah, y=0 di alas model, z=0 tengah) — konsisten
  // dengan konvensi geometri hardcoded yang sudah ada.
  const scaledBox = new THREE.Box3().setFromObject(wrapper);
  const center = new THREE.Vector3();
  scaledBox.getCenter(center);

  wrapper.position.x -= center.x;
  wrapper.position.y -= scaledBox.min.y; // alas model jadi y=0
  wrapper.position.z -= center.z;

  return wrapper;
}

/**
 * Buat geometri "loading spinner" 3D sederhana — ring biru berputar,
 * ditampilkan sebagai placeholder selama model .glb masih di-fetch.
 * Dikembalikan beserta fungsi `tick(deltaSeconds)` untuk dipanggil di
 * render loop supaya animasinya berjalan.
 */
export function createLoadingSpinner(targetWidth, targetHeight, targetDepth) {
  const group = new THREE.Group();
  const radius = Math.max(targetWidth, targetDepth, 0.1) * 0.3;

  const ringGeo = new THREE.TorusGeometry(radius, radius * 0.08, 8, 32, Math.PI * 1.5);
  const ringMat = new THREE.MeshBasicMaterial({ color: "#3b82f6", transparent: true, opacity: 0.85 });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.rotation.x = Math.PI / 2;
  ring.position.y = Math.max(targetHeight, 0.1) / 2;
  group.add(ring);

  // Outline box tipis menandai area yang akan ditempati model asli,
  // supaya user tetap dapat gambaran ukuran device sebelum model siap.
  const boxGeo = new THREE.BoxGeometry(targetWidth || 0.2, targetHeight || 0.2, targetDepth || 0.2);
  const edges = new THREE.EdgesGeometry(boxGeo);
  const boxLine = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: "#3b82f6", transparent: true, opacity: 0.3 }));
  boxLine.position.y = (targetHeight || 0.2) / 2;
  group.add(boxLine);

  const tick = (deltaSeconds) => {
    ring.rotation.z += deltaSeconds * 3;
  };

  return { group, tick };
}